// TODO update this to import from commmons
// import { gql } from '@reverbdotcom/commons/src/gql';
// eslint-disable-next-line no-restricted-imports
import { gql } from '@apollo/client';

import React from 'react';
import { Item2MetadataData, Category } from '@reverbdotcom/commons/src/gql/graphql';
import Metadata from '@reverbdotcom/commons/src/components/metadata';
import { renderAvailability } from '@reverbdotcom/commons/src/components/listing_offer_metadata';
import URLHelpers, { ITEM_WITH_SLUG_ROUTE } from './shared/url_helpers';
import { getBrandBreadcrumbParams, getCategories } from './item2_breadcrumbs';
import { CORE_WEB_ORIGIN } from '@reverbdotcom/env';

const ADDITIONAL_TYPE_URL = 'http://productontology.org/doc/Musical_instrument';
const AGGREGATE_RATING_TYPE_URL = 'https://schema.org/AggregateRating';
const OFFER_TYPE_URL = 'https://schema.org/Offer';

interface IProps {
  listing: Item2MetadataData.Fragment;
}

export const Item2MetadataFragment = gql`
  fragment Item2MetadataData on Listing {
    _id
    id
    title
    description
    finish
    make
    model
    slug
    inventory
    state
    brandSlug
    categories {
      _id
      id
      name
      rootSlug
      cmsLink {
        href
      }
    }
    condition {
      conditionSlug
    }
    pricing {
      buyerPrice {
        amount
        currency
      }
    }
    preorderInfo {
      onPreorder
    }
    csp {
      _id
      productReviewSearch(
        input: {
          limit: 0
        }
      ) {
        stats {
          total
          averageRating
        }
      }
    }
    shop {
      _id
      name
      onVacation
    }
    metaImage: images(input: { type: "Product", scope: "photos", transform: "large", count: 25 }) {
      source
    }
    experiments {
      name
      bucket
    }
  }
`;

function listingPath(listing: Item2MetadataData.Fragment): string {
  return ITEM_WITH_SLUG_ROUTE.expand({
    slug: listing.slug,
    id: listing.id,
  });
}

export function getURL(path: string): string {
  const url = new URL(path, CORE_WEB_ORIGIN);
  return url.href;
}

function itemConditionUrl(condition: string): string {
  if (condition === 'brand-new') {
    return 'https://schema.org/NewCondition';
  }

  return 'https://schema.org/UsedCondition';
}

function renderAggregateRating(csp) {
  const stats = csp?.productReviewSearch?.stats;

  if (!stats) { return null; }

  return (
    <div
      itemProp="aggregateRating"
      itemType={AGGREGATE_RATING_TYPE_URL}
      itemScope
    >
      <meta
        itemProp="ratingValue"
        content={`${Math.round(stats.averageRating * 10) / 10}`}
      />
      <meta
        itemProp="ratingCount"
        content={`${stats.total}`}
      />
      <meta
        itemProp="bestRating"
        content="5"
      />
      <meta
        itemProp="worstRating"
        content="0"
      />
    </div>
  );
}

function renderOffers(listing: Item2MetadataData.Fragment) {
  const categories = getCategories(listing);

  return (
    <div itemProp="offers" itemScope itemType={OFFER_TYPE_URL}>
      {renderAvailability(listing)}
      <meta itemProp="price" content={listing.pricing.buyerPrice.amount} />
      <meta itemProp="priceCurrency" content={listing.pricing.buyerPrice.currency} />
      <meta itemProp="url" content={getURL(listingPath(listing))}></meta>
      {categories.map((category) => { return <meta key={category.id} itemProp="category" content={category.name} />; })}
    </div>
  );
}

function renderBreadcrumbCategories(category: Category, idx: number) {
  return (
    <li
      key={category.id}
      itemProp="itemListElement"
      itemScope
      itemType="http://schema.org/ListItem"
    >
      <a
        href={category.cmsLink.href && getURL(category.cmsLink.href)}
        itemProp="item"
      >
        <span itemProp="name">
          {category.name}
        </span>
      </a>
      <meta itemProp="position" content={`${idx + 1}`} />
    </li>
  );
}

export default function Item2Metadata(props: IProps) {
  const { listing } = props;
  const metaImages = listing.metaImage ?? [];

  const categories = getCategories(listing);

  return (
    <div
      itemScope
      itemType="http://schema.org/Product"
    >
      <Metadata
        title={listing.title}
        description={listing.description}
        image={metaImages[0]?.source}
      />
      <meta itemProp="url" content={getURL(listingPath(listing))}></meta>
      <link itemProp="additionalType" href={ADDITIONAL_TYPE_URL}></link>
      <meta itemProp="description" content={listing.description}></meta>
      <meta itemProp="model" content={listing.model}></meta>
      <link itemProp="itemCondition" href={itemConditionUrl(listing.condition.conditionSlug)}></link>
      <meta itemProp="color" content={listing.finish}></meta>
      <meta itemProp="name" content={listing.title}></meta>
      {metaImages.map((image) => {
        return <meta key={image.source} itemProp="image" content={image.source}></meta>;
      })}
      <div itemProp="brand" itemType="https://schema.org/Brand" itemScope>
        <meta itemProp="name" content={listing.make} />
      </div>
      {renderAggregateRating(listing.csp)}
      {renderOffers(listing)}
      <ul
        style={{ display: 'none' }}
        itemScope
        itemType="http://schema.org/BreadcrumbList"
      >
        {categories.map((category, idx) => renderBreadcrumbCategories(category, idx))}
        <li
          itemProp="itemListElement"
          itemScope
          itemType="http://schema.org/ListItem"
        >
          <a
            href={getURL(URLHelpers.brandPath(listing.brandSlug, getBrandBreadcrumbParams(listing)))}
            itemProp="item"
          >
            <span itemProp="name">
              {listing.make}
            </span>
          </a>
          <meta itemProp="position" content={`${categories.length + 1}`} />
        </li>
      </ul>
    </div>
  );
}
